import { useEffect, useMemo, useState } from "react";

import { apiRequest, ensureCsrfToken } from "../api";

import "../css/MappingHeadersView.css";

import Swal from "sweetalert2";

const MAPPING_TYPES = [
  { key: "system_variables", label: "System Variables" },
  { key: "vendor_master", label: "Vendor Master" },
];

function buildEmptySelections(context) {
  return {
    system_variables: Object.fromEntries(
      (context.system_variables || []).map((item) => [
        item.name,
        {
          po_header: "",
          po_mapping_type: "",
          miro_header: "",
          miro_mapping_type: "",
          
        },
      ]),
    ),
    vendor_master: Object.fromEntries(
        (context.vendor_system_variables || []).map((item) => [
            item.name,
            {
                header: "",
                mapped_type: "",
            },
        ]),
    ),
    
  };
}

export default function InvCommonMappingHeadersView() {
  const [context, setContext] = useState(null);
  const [activeType, setActiveType] = useState("system_variables");
  const [selections, setSelections] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [matchingType, setMatchingType] = useState(null);
  
  const [blockingActivate, setBlockingActivate] = useState({});
  const [validationRows, setValidationRows] = useState(new Set());

  function getMigoHeaderValue(mapped) {
    return mapped?.miro_header || mapped?.migo_header || "";
  }



  function confirmSystemSuggestion(variableName, field) {
    const mappingTypeField =
      field === "po_header"
        ? "po_mapping_type"
        : "miro_mapping_type";

    setSelections((current) => ({
      ...current,
      system_variables: {
        ...current.system_variables,
        [variableName]: {
          ...current.system_variables[variableName],
          [mappingTypeField]: "mapped",
        },
      },
    }));
  }


  const currentRows = useMemo(() => {
    if (!context) {
      return [];
    }

    if (activeType === "system_variables") {
      return context.invsystem_variables || [];
    }

    if (activeType === "vendor_master") {
      return context.vendor_system_variables || [];
    }

    
    return [];
  }, [activeType, context]);

  const optionsByType = useMemo(() => {
    if (!context) {
      return {};
    }

    return {
      system_variables: {
        migo: context.miro_headers || [],
        po: context.po_headers || [],
      },
      vendor_master: context.vendorerp_headers || [],
      sac_master: context.sac_master_headers || [],
      wholdtax_master: context.wholdtax_master_headers || [],
      gsttax_master: context.gsttax_master_headers || [],
      hsn_master: context.hsn_master_headers || [],
    };
  }, [context]);

  useEffect(() => {
    let cancelled = false;

    async function loadContext() {
      setLoading(true);
      setError("");

      try {
        const response = await apiRequest("/mapping/context/");
        if (cancelled) {
          return;
        }

        const nextContext = response.data;
        setContext(nextContext);
        setSelections(buildEmptySelections(nextContext));
      } catch (requestError) {
        if (!cancelled) {
          setError(requestError.message || "Unable to load mapping context.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadContext();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadSavedMapping() {
      if (!context || !selections) {
        return;
      }

      setStatus("");
      setError("");

      try {
        const csrfToken = await ensureCsrfToken();
        const response = await fetch("/api/inv/mapping/data/", {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": csrfToken,
          },
          body: JSON.stringify({ type: activeType }),
        });

        const payload = await response.json();
        if (!response.ok || !payload.success) {
          throw new Error(payload.error || "Unable to load saved mappings.");
        }

        if (cancelled) {
          return;
        }

        setSelections((current) => ({
          ...current,
          [activeType]:
            activeType === "system_variables"
              ? {
                  ...current[activeType],
                  ...payload.data,
                }
              : {
                  ...current[activeType],
                  ...payload.data,
                },
        }));

        setMatchingType(payload.matching_type || null);
        
        setBlockingActivate(payload.blocking_activate || {});
      } catch (requestError) {
        if (!cancelled) {
          setError(requestError.message || "Unable to load saved mappings.");
        }
      }
    }

    loadSavedMapping();

    return () => {
      cancelled = true;
    };
  }, [activeType, context, selections ? "ready" : "empty"]);

  function updateSystemSelection(variableName, field, value) {
    setSelections((current) => {
      const mappingTypeField =
        field === "po_header"
          ? "po_mapping_type"
          : field === "miro_header"
          ? "miro_mapping_type"
          : null;

      return {
        ...current,
        system_variables: {
          ...current.system_variables,
          [variableName]: {
            ...current.system_variables[variableName],
            [field]: value,
            ...(mappingTypeField
              ? {
                  [mappingTypeField]: value ? "mapped" : "",
                }
              : {}),
          },
        },
      };
    });
  }

  function updateSingleSelection(type, variableName, value) {
    setSelections((current) => ({
      ...current,
      [type]: {
        ...current[type],
        [variableName]: {
          header: value,
          mapped_type: value ? "mapped" : "",
        },
      },
    }));
  }

  function validateSystemVariables() {
    const rows = selections.system_variables;
    const errors = [];
    const invalidRows = [];

    currentRows.forEach((row) => {
      const mapped = rows[row.name] || {};

      if (row.mandatory && !getMigoHeaderValue(mapped)) {
        errors.push(`${row.name}: MIGO header is required`);
        invalidRows.push(row.name);
      }

      
    });

    return { errors, invalidRows };
  }

  function validateMasterSelections(type) {
    const errors = [];
    const rows = selections[type];
    const invalidRows = [];

    currentRows.forEach((row) => {
      if (row.mandatory && !rows[row.name]) {
        errors.push(`${row.name}: mapping is required`);
        invalidRows.push(row.name);
      }
    });

    if (type === "vendor_master" && blockingActivate.account_blocking_activate === "Y" && !rows["VendorBlocked(Y/N)"]) {
      errors.push("VendorBlocked(Y/N): mapping is required when Vendor Master blocking is active");
      invalidRows.push("VendorBlocked(Y/N)");
    }

    return { errors, invalidRows };
  }

  function buildSavePayload() {
    if (activeType === "system_variables") {
      return {
        type: activeType,
        mappings: currentRows.map((row) => ({
          system_variable: row.name,
          po_header: selections.system_variables[row.name]?.po_mapping_type === "mapped"
                      ? selections.system_variables[row.name]?.po_header || ""
                      : "",
          migo_header:
                      selections.system_variables[row.name]?.miro_mapping_type === "mapped"
                          ? getMigoHeaderValue(selections.system_variables[row.name])
                          : "",
        })),
        mappingss: {},
      };
    }

    return {
      type: activeType,
      mappings: [],
      mappingss: Object.fromEntries(
          Object.entries(selections[activeType]).map(([key, value]) => [
              key,
              value?.mapped_type === "mapped"
                  ? value.header
                  : "",
          ]),
      ),
    };
  }

  async function handleSave() {
    setSaving(true);
    setError("");

    try {
      const validationResult =
        activeType === "system_variables"
          ? validateSystemVariables()
          : validateMasterSelections(activeType);

      setValidationRows(new Set(validationResult.invalidRows));

      if (validationResult.errors.length) {
        if (validationResult.invalidRows.length) {
          document
            .querySelector(
              `[data-row="${validationResult.invalidRows[0]}"]`
            )
            ?.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
        }

        throw new Error(validationResult.errors.join(" | "));
      }

      const csrfToken = await ensureCsrfToken();

      const response = await fetch("/api/inv/mapping/save/", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
        body: JSON.stringify(buildSavePayload()),
      });

      const payload = await response.json();

      if (!response.ok || payload.status !== "success") {
        throw new Error(payload.message || "Unable to save mappings.");
      }

      setValidationRows(new Set());

      await Swal.fire({
        icon: "success",
        title: "Success",
        text: payload.message || "Mappings saved successfully.",
        confirmButtonText: "OK",
      });

    } catch (requestError) {

      await Swal.fire({
        icon: "error",
        title: "Save Failed",
        text: requestError.message || "Unable to save mappings.",
        confirmButtonText: "OK",
      });

      setError(requestError.message || "Unable to save mappings.");

    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div className="workspace-panel">Loading header mapping…</div>;
  }

  if (!context || !selections) {
    return <div className="workspace-panel">Mapping context is unavailable.</div>;
  }

  return (
    <section className="mapping-view">
      <div className="workspace-panel mapping-view__hero">
        <div>
          <p className="eyebrow">Header Mapping</p>
          <h2>Mapping Configuration</h2>
          <p className="config-view__intro">
            Match uploaded ERP headers to the system variables and master fields used by the Django
            backend validations.
          </p>
        </div>

        <div className="mapping-view__controls">
          <label className="config-field">
            <span>Mapping Type</span>
            <select value={activeType} onChange={(event) => setActiveType(event.target.value)}>
              {MAPPING_TYPES.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <button className="primary-button" type="button" disabled={saving} onClick={handleSave}>
            {saving ? "Saving..." : "Save Mapping"}
          </button>
        </div>
      </div>

      {activeType === "system_variables" ? (
        <div className="mapping-note-card">
          <strong>Important:</strong>
          <ol className="mapping-note-list">
            <li>
              It is recommended to map all the fields which are mandatory as per your ERP.
              Otherwise, the accounting entry will be <strong>rejected</strong> upon uploading the
              template.
            </li>
            <li>
              In addition to above, fields which are marked with <strong>*</strong> are
              <strong> mandatory</strong> to map.
            </li>
            <li>
              Fields which are marked with <strong>**</strong> are <strong>conditional mandatory</strong>
              {" "}to map depending on basic configuration settings.
            </li>
          </ol>
        </div>
      ) : null}

      {status ? <div className="config-banner config-banner--success">{status}</div> : null}
      {error ? <div className="config-banner config-banner--error">{error}</div> : null}

      <div className="workspace-panel">
        <div className="config-table">
          {activeType === "system_variables" ? (
            <table>
              <thead>
                <tr>
                  <th>System Variable</th>
                  <th>MIGO Header</th>
                  <th>PO Header</th>
                </tr>
              </thead>
              <tbody>
                {currentRows.map((row) => (
                  <tr
                    key={row.name}
                    data-row={row.name}
                    className={validationRows.has(row.name) ? "mapping-row-error" : ""}
                  >
                    <td>
                      <div className="mapping-variable">
                        <span>{row.name}</span>
                        {row.mandatory ? <span className="mandatory-star">*</span> : null}
                        {row.conditional_mandatory ? <span className="mandatory-star">**</span> : null}
                      </div>
                      {row.tooltip ? <div className="mapping-tooltip">{row.tooltip}</div> : null}
                    </td>
                    <td>
                      <select
                        className="mapping-select mapping-select--migo"
                        value={selections.system_variables[row.name]?.miro_header || ""}
                        onChange={(event) =>
                          updateSystemSelection(row.name, "miro_header", event.target.value)
                        }
                      >
                        <option value="">-- Select MIGO Header --</option>
                        {(optionsByType.system_variables.migo || []).map((header) => (
                          <option key={header} value={header}>
                            {header}
                          </option>
                        ))}
                      </select>

                      {selections.system_variables[row.name]?.miro_mapping_type === "mapped" && (
                        <span className="mapping-badge mapping-badge--mapped">
                          ✓ Saved
                        </span>
                      )}

                      {selections.system_variables[row.name]?.miro_mapping_type === "suggestion" && (
                        <span
                          className="mapping-badge mapping-badge--suggested mapping-badge--clickable"
                          onClick={() => confirmSystemSuggestion(row.name, "miro_header")}
                          title="Click to accept this suggestion"
                        >
                          💡 Suggested
                        </span>
                      )}
                    </td>
                    <td>
                    <select
                      className="mapping-select mapping-select--po"
                      value={selections.system_variables[row.name]?.po_header || ""}
                      onChange={(event) =>
                        updateSystemSelection(row.name, "po_header", event.target.value)
                      }
                    >
                      <option value="">-- Select PO Header --</option>
                      {(optionsByType.system_variables.po || []).map((header) => (
                        <option key={header} value={header}>
                          {header}
                        </option>
                      ))}
                    </select>

                    {selections.system_variables[row.name]?.po_mapping_type === "mapped" && (
                      <span className="mapping-badge mapping-badge--mapped">
                        ✓ Saved
                      </span>
                    )}

                    {selections.system_variables[row.name]?.po_mapping_type === "suggestion" && (
                      <span
                        className="mapping-badge mapping-badge--suggested mapping-badge--clickable"
                        onClick={() => confirmSystemSuggestion(row.name, "po_header")}
                        title="Click to accept this suggestion"
                      >
                        💡 Suggested
                      </span>
                    )}
                  </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>System Variable</th>
                  <th>ERP Header</th>
                </tr>
              </thead>
              <tbody>
                {currentRows.map((row) => (
                  <tr key={row.name}>
                    <td>
                      <div className="mapping-variable">
                        <span>{row.name}</span>
                        {row.mandatory ? <span className="mandatory-star">*</span> : null}
                        {row.conditional_mandatory ? <span className="mandatory-star">**</span> : null}
                      </div>
                      {row.tooltip ? <div className="mapping-tooltip">{row.tooltip}</div> : null}
                    </td>
                    <td>
                      <select
                        value={selections[activeType][row.name]?.header || ""}
                        onChange={(event) =>
                          updateSingleSelection(activeType, row.name, event.target.value)
                        }
                      >
                        <option value="">-- Select Header --</option>
                        {(optionsByType[activeType] || []).map((header) => (
                          <option key={header} value={header}>
                            {header}
                          </option>
                        ))}
                      </select>
                      {selections[activeType][row.name]?.mapped_type === "mapped" && (
                          <span className="mapping-badge mapping-badge--mapped">
                              ✓ Saved
                          </span>
                      )}

                      {selections[activeType][row.name]?.mapped_type === "suggestion" && (
                          <span className="mapping-badge mapping-badge--suggested">
                              💡 Suggested
                          </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </section>
  );
}
